import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { TTMMapData } from '../../models/ttm-map.model';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ExploreModeService } from '../../services/explore_mode.service';
import { TTMMapResponse } from '../../models/ttm-map.model';


interface FilterRule {
  filter: string;
  type: string;
  operator: string;
  values: string[];
}

@Component({
  selector: 'app-edit-ttm-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatCheckboxModule,
    MatOptionModule,
    MatSnackBarModule,

  ],
  templateUrl: './edit-ttm-dialog.component.html',
  styleUrls: ['./edit-ttm-dialog.component.scss']
})
export class EditTtmDialogComponent implements OnInit {
  formData: any;
  fileName!: string;

  selectedGlobalRecordFilter: string = '';
  selectedSourceAttribute: string = '';
  currentValue: string = '';

  notNullState: { [attribute: string]: boolean } = {};

  filterOptions: string[] = [
    'NOT IN', 'IN', 'NOT LESS THAN', 'LESS THAN', 'NOT GREATER THAN', 'GREATER THAN'
  ];

  filterDataTypes: { [key: string]: string[] } = {
    'NOT NULL': ['Boolean'],
    'NOT IN': ['List'],
    'IN': ['List'],
    'NOT LESS THAN': ['Number'],
    'LESS THAN': ['Number'],
    'NOT GREATER THAN': ['Number'],
    'GREATER THAN': ['Number']
  };

  operatorOptions: string[] = ['AND', 'OR'];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: TTMMapData,
    private dialogRef: MatDialogRef<EditTtmDialogComponent>,
    private snackBar: MatSnackBar,
    private exploreModeService: ExploreModeService
  ) { }

  ngOnInit(): void {

    if (!this.data || !Object.keys(this.data).length) {
      console.error('No data provided to EditTtmDialogComponent');
      this.formData = {};   // Safeguard downstream usage
      return;
    }

    const [fileName] = Object.keys(this.data);
    this.fileName = fileName;
    this.formData = JSON.parse(JSON.stringify(this.data[fileName] ?? {}));

    if (!this.formData.targetMetaData) {
      this.formData.targetMetaData = {};
    }
    console.log('Incoming mapTo:', this.formData.targetMetaData.mapTo);

    if (!this.formData.targetMetaData.mapTo) {
      this.formData.targetMetaData.mapTo = '';
    }

    if (!this.formData.sourceMetaData) {
      this.formData.sourceMetaData = {};
    }
    if (!this.formData.filters) {
      this.formData.filters = {};
    }
    if (!this.formData.filtersNotNull) {
      this.formData.filtersNotNull = {};
    }






    // Initialize notNullState safely
    Object.keys(this.formData.sourceMetaData).forEach(attr => {
      this.notNullState[attr] = this.formData.filtersNotNull[attr] || false;
    });
  }



  objectKeys(obj: any): string[] {
    if (!obj) {
      return [];
    }
    return Object.keys(obj);
  }


  getAttributeFilters(attribute: string): FilterRule[] {
    if (!this.formData.filters[attribute]) {
      this.formData.filters[attribute] = [];
    }
    return this.formData.filters[attribute];
  }

  isFilterDisabled(attribute: string, currentRule: FilterRule, filter: string): boolean {
    const selectedFilters = this.getAttributeFilters(attribute)
      .filter(rule => rule !== currentRule)
      .map(rule => rule.filter);
    return selectedFilters.includes(filter);
  }

  addFilterRule(attribute: string): void {
    this.getAttributeFilters(attribute).push({
      filter: '',
      type: '',
      operator: '',
      values: []
    });
  }

  removeFilterRule(attribute: string, index: number): void {
    this.formData.filters[attribute].splice(index, 1);
  }

  onFilterChange(rule: FilterRule): void {
    rule.type = '';
    rule.values = [];
    rule.operator = '';
  }

  onNotNullChange(attribute: string): void {
    this.formData.filtersNotNull[attribute] = this.notNullState[attribute];
  }

  addValueToRule(event: Event, rule: FilterRule): void {
    event.preventDefault();
    const trimmedValue = this.currentValue.trim();
    if (trimmedValue && !rule.values.includes(trimmedValue)) {
      rule.values.push(trimmedValue);
      this.currentValue = '';
    }
  }

  removeValueFromRule(rule: FilterRule, index: number): void {
    rule.values.splice(index, 1);
  }

  saveCurrentFilter(): void {
    if (this.selectedSourceAttribute) {
      const filters = this.formData.filters[this.selectedSourceAttribute] || [];
      const nonEmptyFilters: FilterRule[] = filters.filter((rule: FilterRule) => rule.filter && rule.filter.trim() !== '');
      this.formData.filters[this.selectedSourceAttribute] = nonEmptyFilters;
      this.formData.filters[this.selectedSourceAttribute].push({
        filter: '',
        type: '',
        operator: '',
        values: []
      });

      console.log(`Filters for ${this.selectedSourceAttribute} saved:`, this.formData.filters[this.selectedSourceAttribute]);

      // 🟢 Show success snackbar!
      this.snackBar.open('Filter saved successfully!', 'Close', {
        duration: 2000,
        panelClass: ['bg-green-600', 'text-white']  // Optional custom styling
      });
    }
  }



  onSourceAttributeChange(): void {
    // Automatically create at least one filter rule if none exist
    const filters = this.formData.filters[this.selectedSourceAttribute];
    if (!filters || filters.length === 0) {
      this.addFilterRule(this.selectedSourceAttribute);
    }
  }



  save(): void {
    this.saveCurrentFilter();

    const apiPayload: any = {
      listOfFiles: [this.fileName],
      mergeTargetAttribute: []
    };

    const fileData: any = {};

    // 1️⃣ Global Record Filter
    fileData.globalRecordFilter = {};
    if (this.selectedGlobalRecordFilter) {
      fileData.globalRecordFilter.selectedAttribute = this.selectedGlobalRecordFilter;
    }

    // 2️⃣ Source Metadata
    const sourceMetaData = this.formData.sourceMetaData;
    fileData.sourceMetadata = [];
    Object.keys(sourceMetaData).forEach(attr => {
      const fieldInfo = sourceMetaData[attr];
      const field: any = {};
      if (fieldInfo.dataType && fieldInfo.dataType.trim() !== '') {
        field.dataType = fieldInfo.dataType;
      }
      if (fieldInfo.length && fieldInfo.length.trim() !== '') {
        field.length = fieldInfo.length;
      }
      if (fieldInfo.scale && fieldInfo.scale.trim() !== '') {
        field.scale = fieldInfo.scale;
      }
      if (fieldInfo.precision && fieldInfo.precision.trim() !== '') {
        field.precision = fieldInfo.precision;
      }
      if (fieldInfo.sourceDateFormat && fieldInfo.sourceDateFormat.trim() !== '') {
        field.sourceDateFormat = fieldInfo.sourceDateFormat;
      }

      if (Object.keys(field).length > 0) {
        const fieldItem: any = {};
        fieldItem[attr] = field;
        fileData.sourceMetadata.push(fieldItem);
      }
    });

    // 3️⃣ Filter per Attribute
    const filters: any = {};
    const numericFilters = ['NOT LESS THAN', 'LESS THAN', 'NOT GREATER THAN', 'GREATER THAN'];
    const allAttributes = new Set([
      ...Object.keys(this.formData.filtersNotNull || {}),
      ...Object.keys(this.formData.filters || {})
    ]);

    allAttributes.forEach(attr => {
      const attributeFilter: any = {};
      const filtration: any = {};

      const rules = this.formData.filters[attr] || [];
      const firstOperator = rules.find((rule: { operator: string; }) => rule.operator && rule.operator.trim() !== '');
      attributeFilter.operator = firstOperator ? firstOperator.operator : 'AND';

      if (this.formData.filtersNotNull && this.formData.filtersNotNull[attr]) {
        filtration['NOT NULL'] = true;
      }

      rules.forEach((rule: FilterRule) => {
        if (rule.filter && rule.filter.trim() !== '') {
          if (numericFilters.includes(rule.filter)) {
            if (rule.values && rule.values.length > 0) {
              const numValue = Number(rule.values[0]);
              if (!isNaN(numValue)) {
                filtration[rule.filter] = numValue;
              }
            }
          } else {
            filtration[rule.filter] = (rule.values || []).filter(v => v.trim() !== '');
          }
        }
      });

      if (Object.keys(filtration).length > 0) {
        attributeFilter.filtration = filtration;
        filters[attr] = attributeFilter;
      }
    });

    if (Object.keys(filters).length > 0) {
      fileData.filter = filters;
    }

    // 4️⃣ Target Metadata
    const targetMetaData = this.formData.targetMetaData;
    fileData.targetMetadata = [];
    const targetMetaItem: any = {};

    if (targetMetaData.attributes && targetMetaData.attributes.length > 0) {
      targetMetaItem.attributes = targetMetaData.attributes;
    }

    if (targetMetaData.mapTo) {
      targetMetaItem.mapTo = (targetMetaData.mapTo as string)
        .split(',')
        .map((item: string) => item.trim())
        .filter((item: string) => item.length > 0);
    }

    if (
      targetMetaData.dataType &&
      typeof targetMetaData.dataType === 'string' &&
      targetMetaData.dataType.trim() !== ''
    ) {
      targetMetaItem.dataType = targetMetaData.dataType.trim();
    }

    if (Object.keys(targetMetaItem).length > 0) {
      fileData.targetMetadata.push(targetMetaItem);
    }

    apiPayload[this.fileName] = fileData;

    console.log('Generated API Payload:', apiPayload);

    // 🔥 Send the PUT request
    const sessionid: string = localStorage.getItem('sessionid') || '';
    if (!sessionid) {
      console.error('Session ID is missing');
      return;
    }

    this.exploreModeService.SaveTTMMap(sessionid, apiPayload).subscribe({
      next: (response: TTMMapResponse) => {
        console.log('TTM Map saved successfully:', response);
        this.snackBar.open('TTM Map saved successfully', 'Close', {
          duration: 2000,
          panelClass: ['bg-green-600', 'text-white']  // Optional custom styling
        });
        this.dialogRef.close(response);
      },
      error: (err) => {
        console.error('Failed to save TTM Map:', err);
      }
    });
  }



  cancel(): void {
    this.dialogRef.close();
  }
}
